import { createFileRoute, notFound } from "@tanstack/react-router";
import { INDUSTRIES } from "@/content/industries";
import { IndustryPageTemplate } from "@/components/industries/IndustryPageTemplate";
import { pageMeta, ldScripts, breadcrumbLd, faqLd, serviceLd, localBusinessLd } from "@/lib/seo";
import { siteConfig } from "@/config/site";


export const Route = createFileRoute("/industries/$slug")({
  loader: async ({ params }) => {
    const { getIndustry, getAustinAngle } = await import("@/content/industries");
    const industry = getIndustry(params.slug);
    if (!industry) throw notFound();
    return { industry, austin: getAustinAngle(params.slug) ?? null };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: `Industry not found — ${siteConfig.name}` },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const i = loaderData.industry;
    const path = `/industries/${params.slug}/`;
    const austin = loaderData.austin;
    const base = pageMeta({
      path,
      title: i.metaTitle,
      description: i.metaDescription,
      type: "article",
    });
    return {
      ...base,
      scripts: ldScripts(
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Industries", path: "/industries/" },
          { name: i.name, path },
        ]),
        serviceLd({
          name: `${i.name} SEO — ${siteConfig.contact.address.city} & Central ${siteConfig.contact.address.state}`,
          description: i.metaDescription,
          path,
        }),
        localBusinessLd({
          city: siteConfig.contact.address.city,
          region: siteConfig.contact.address.state,

          path,
          description: `${i.name} SEO programmes led from ${siteConfig.contact.address.city}, ${siteConfig.contact.address.state} by founder ${siteConfig.founder.name} — serving Central ${siteConfig.contact.address.state} and beyond.`,
          latLng: siteConfig.contact.address.coordinates,
          areaServed: austin?.serviceAreas ?? ["Round Rock", "Cedar Park", "Georgetown", "San Marcos", "Leander"],

        }),
        ...(i.faqs?.length ? [faqLd(i.faqs)] : []),
      ),
    };
  },
  notFoundComponent: IndustryNotFound,
  errorComponent: IndustryError,
  component: IndustryPage,
});

function IndustryPage() {
  const { industry } = Route.useLoaderData();
  return <IndustryPageTemplate industry={industry} />;
}

function IndustryNotFound() {
  return (
    <div className="container-page py-24">
      <h1 className="font-display text-4xl">Industry not found</h1>
      <p className="mt-3 text-muted-foreground">
        We may not cover that vertical yet. See all industries below.
      </p>
      <ul className="mt-6 space-y-2">
        {INDUSTRIES.map((i) => (
          <li key={i.slug} className="text-primary">
            <a href={`/industries/${i.slug}`}>{i.name}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function IndustryError({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  return (
    <div className="container-page py-24">
      <h1 className="font-display text-4xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{(error as Error).message}</p>
      <button
        onClick={reset}
        className="mt-6 rounded-full border border-border-strong px-5 py-2 text-sm hover:border-primary hover:text-primary"
      >
        Retry
      </button>
    </div>
  );
}
